import styled from 'styled-components'

export const WorksRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

export const PlatformBlock = styled.section`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

// BRAND-9490 review fix: the arrows previously sat in-flow inside this row
// (via `WorkArrowGroup` with `margin-left: auto`), which made the flex
// row's height equal to the TALLEST child — the 2rem (32px) arrow buttons —
// growing this header from its normal ~18-20px (icon/text line height) to
// ~32px whenever arrows show (works.length > 3). That directly violated the
// BRAND-9490 AC ("carousel must not increase the vertical space the section
// currently takes up") the original absolutely-positioned slick arrows were
// built to satisfy. `position: relative` here (paired with `WorkArrowGroup`
// below being `position: absolute`) restores that: the arrows are taken out
// of flow, so this row's height is once again driven only by the icon/handle
// regardless of whether the arrows render. `padding-right` (applied only
// when arrows are present, via `$hasArrows`) reserves the arrow group's
// footprint so a long handle truncates before running under the circles
// instead of butting up against/overlapping them.
export const PlatformHeader = styled.div<{ $hasArrows?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-right: ${({ $hasArrows }) => ($hasArrows ? '4.5rem' : 0)};
  font-family: Rubik, verdana, sans-serif;
`

// QA follow-up (BRAND-9490, ticket comment 102422): houses the carousel's
// prev/next arrows at the upper-right of the channel-handle row (see
// `PlatformHeader` above).
//
// Review fix (BRAND-9490): this used to be an in-flow flex child pinned to
// the far edge via `margin-left: auto`, which made it contribute its own
// 2rem height to the header row (see `PlatformHeader` comment above) —
// growing the section's vertical footprint whenever arrows show, which
// broke the "no added height" AC. Now `position: absolute; right: 0`,
// vertically centered on the header's own line via `top: 50%` +
// `translateY(-50%)` (with `PlatformHeader` as the `position: relative`
// anchor), so it overlays the header's right edge and contributes ZERO flow
// height — the header's height is exactly the same with or without arrows.
// The 2rem (32px) buttons are taller than the ~18-20px header line, so once
// centered they overshoot the header's top/bottom edges by roughly
// (32 - lineHeight) / 2 ≈ 6-7px each side; `PlatformBlock`'s `gap: 1rem`
// (16px) between this header and the slider comfortably absorbs that
// overshoot without touching the first thumbnail row. `flex-shrink: 0` is
// kept as a defensive no-op: harmless now that this is out of flex flow, but
// cheap insurance if this ever goes back to being an in-flow child.
export const WorkArrowGroup = styled.div`
  position: absolute;
  top: 50%;
  right: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
  transform: translateY(-50%);
`

// Review fix (BRAND-9490): `min-width: 0` overrides the flex-item default of
// `min-width: auto` (which for a `white-space: nowrap` box resolves to the
// full unbroken text width, refusing to shrink below it) — without it, a
// long handle would either force this row wider than its container or wrap
// to a second line (itself an AC-breaking height increase). `overflow:
// hidden` + `text-overflow: ellipsis` + `white-space: nowrap` truncate the
// handle instead once `PlatformHeader`'s reserved `padding-right` leaves it
// less room than the text needs, so a long handle can never run under (or
// push out) the arrows in `WorkArrowGroup`.
export const PlatformHandle = styled.span`
  display: block;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 0.875rem;
  font-weight: 600;
  color: #4d4d4d;
`

export const PlatformStats = styled.span`
  font-size: 0.8125rem;
  color: #b3b3b3;
`

// Review fix (BRAND-9490): when `creator.creator_data.url` is set, THIS
// anchor — not the `PlatformHandle` span inside it — is the actual flex
// child of `PlatformHeader`, so it needs the same `min-width: 0` +
// `overflow: hidden` as `PlatformHandle` for the flex item itself to be
// allowed to shrink/truncate rather than pushing the row wider. `display:
// block` blockifies this (anchors are inline by default) so its box model
// behaves like a normal shrinkable flex item.
export const HandleLink = styled.a`
  display: block;
  min-width: 0;
  overflow: hidden;
  color: inherit;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

export const WorkThumbLink = styled.a`
  display: block;
  cursor: pointer;
  color: inherit;
  text-decoration: none;
`

// Wraps the react-slick carousel. `.slick-list`/`.slick-slide > div` reproduce
// the previous CSS-grid `gap: 1rem` via the standard negative-margin/padding
// trick (half the gap on each side) so 3 visible cards are pixel-identical in
// width — and therefore height, since WorkThumb is `aspect-ratio: 1 / 1` — to
// the old grid. Keeping the row's own width untouched (no outer margin here)
// is what keeps the section height unchanged versus the old top-3 grid.
//
// NOTE (verified while investigating a BRAND-9490 review comment that
// flagged this as a critical bug): `.slick-slide > div` really does have a
// real `<div>` to land on, and it is NOT `WorkCard`/`WorkBody`/`WorkThumb`.
// react-slick's *top-level* `Slider` component (`slider.js`) pre-processes
// `children` for its `rows`/`slidesPerRow` grid feature BEFORE `Track`
// (`track.js`) ever runs — and it does this unconditionally, even at the
// library defaults `rows: 1, slidesPerRow: 1`. For every slide it wraps our
// child in two nested plain `<div>`s (`newChildren.push(<div>{[<div>{[cloned
// child]}</div>]}</div>)`), THEN `Track.renderSlides` clones the outer of
// those two divs with the `slick-slide` class + computed slide `width` — so
// `slick-slide` lands on Slick's own outer wrapper div, `WorkCard` sits two
// levels deeper (inside Slick's *inner* wrapper div, itself untouched/no
// explicit width), and `.slick-slide > div` unambiguously resolves to
// Slick's own inner wrapper — never to `WorkBody`/`WorkThumb`. Confirmed via
// rendered `outerHTML` in a jsdom test (see SelectedWorks.test.tsx): that
// inner div's computed padding is `0px 0.5rem`, while WorkBody's stays
// `1rem 1.5rem 1.5rem` and WorkThumb's stays empty. This is also why the
// BrandFans submissions carousel (`Submissions/List.styled.ts`) works with
// the same `.slick-slide > div` shape despite its slide child never
// forwarding `className` itself — Slider's own wrapping supplies the div
// regardless of what the slide child is. Do not "fix" this by adding an
// extra manual wrapper `<div>` per slide or by retargeting `.slick-slide`
// directly — either change moves the `slick-slide` class assignment further
// from WorkCard again (Slider re-wraps whatever you pass it) and simply
// removes the padding that creates the gap, with nothing replacing it.
export const WorksSlider = styled.div`
  .slick-list {
    margin: 0 -0.5rem;
  }

  .slick-slide > div {
    padding: 0 0.5rem;
  }
`

// QA follow-up (BRAND-9490, ticket comment 102422): previously
// `position: absolute; top: 50%` inside `WorksSlider`, which placed these
// over the thumbnail edges — low-contrast and easy to miss. Now rendered
// in flow, inside `WorkArrowGroup` in the channel-handle header row, so the
// overlay-specific positioning is gone; the circular button look (size,
// background, shadow, disabled dimming) is unchanged.
export const WorkArrow = styled.div<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: ${({ theme }) => theme.whiteColor};
  box-shadow: 0 0.0625rem 0.375rem rgb(0 0 0 / 16%);
  cursor: pointer;
  opacity: ${({ disabled }) => (disabled ? 0.35 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
`

// Page-dot pagination (BRAND-9490): rendered by hand as a sibling of
// `<Slider>` rather than via react-slick's `dots`/`appendDots`/`customPaging`
// — those clone our markup into react-slick's own `.slick-dots`/`<li>`
// structure, and its default (unscoped) `.slick-dots li` / `li button:before`
// CSS bled through, producing a malformed active dot (oversized, wrong
// color) instead of the intended uniform circle. Styled to match the
// Submission Details carousel's dots
// (`Multiframe/Pagination/Dots/Dot/Dot.styled.tsx`): same circular dot,
// 0.375rem diameter, 0.25rem margin per dot (0.5rem gap), grey400Color idle /
// primaryColor active — color is the ONLY difference between active/idle,
// no size change. That reference wrapper is `position: absolute; bottom:
// -3.75rem` so it costs zero layout height, riding in space the parent
// already reserves below the media; there's no equivalent reserved space
// below this carousel, so this stays in flow instead — margin-top is kept to
// the minimum needed to read as a separate row (see WorksSlider comment
// above re: the "same section height" constraint from the grid→carousel
// change) rather than reproducing the absolute overlay.
export const WorkDotsList = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0.375rem 0 0;
`

export const WorkDot = styled.button<{ active?: boolean }>`
  display: block;
  width: 0.375rem;
  height: 0.375rem;
  margin: 0.25rem;
  padding: 0;
  border: none;
  border-radius: 10rem;
  background: ${({ theme, active }) => (active ? theme.primaryColor : theme.grey400Color)};
  cursor: pointer;
  transition: background 300ms ease;
`

export const WorkCard = styled.article`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 0.5rem;
  border: 0.0625rem solid #e3e3e3;
  background: ${({ theme }) => theme.whiteColor};
`

export const WorkThumb = styled.div`
  position: relative;
  aspect-ratio: 1 / 1;
  overflow: hidden;
  background: ${({ theme }) => theme.grey200Color};

  > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: rgb(77 77 77 / 25%);
    pointer-events: none;
  }
`

export const WorkVideoBadge = styled.span`
  position: absolute;
  bottom: 1.5rem;
  left: 1.5rem;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  color: ${({ theme }) => theme.whiteColor};
`

export const WorkBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem 1.5rem 1.5rem;
  font-family: Rubik, verdana, sans-serif;
`

export const WorkDate = styled.span`
  font-size: 0.8125rem;
  font-weight: 400;
  line-height: 1rem;
  letter-spacing: 0.0187rem;
  color: #808080;
`

export const WorkTitle = styled.p`
  margin: 0;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.25rem;
  letter-spacing: 0.0187rem;
  color: #4d4d4d;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

export const WorkStatRow = styled.div`
  display: flex;
  gap: 1.25rem;
`

export const WorkStat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`

export const WorkStatLabel = styled.span`
  font-size: 0.8125rem;
  font-weight: 400;
  line-height: 1.25rem;
  letter-spacing: 0.0187rem;
  color: #808080;
`

export const WorkStatValue = styled.span`
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1.25rem;
  letter-spacing: 0.0187rem;
  color: #4d4d4d;
`
